import React, { useEffect, useState } from "react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { 
  FileText, 
  Plus, 
  Settings, 
  Users, 
  Search, 
  CreditCard,
  Zap,
  Sparkles,
  Database,
  ArrowRight
} from "lucide-react";
import { useQuotationStore } from "@/stores/quotationStore";
import { useInvoiceStore } from "@/stores/invoiceStore";
import { formatCurrency } from "@/lib/utils";

interface GlobalCommandHUDProps {
  onNavigate: (view: string) => void;
  onCreateQuote: () => void;
  setCurrentQuotation: (q: any) => void;
}

export function GlobalCommandHUD({ onNavigate, onCreateQuote, setCurrentQuotation }: GlobalCommandHUDProps) {
  const [open, setOpen] = useState(false);
  const { quotations } = useQuotationStore();
  const { invoices } = useInvoiceStore();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const runAction = (action: () => void) => {
    action();
    setOpen(false);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <div className="bg-[#0a0a0c]/80 backdrop-blur-3xl border-b border-border p-4">
        <div className="flex items-center gap-2 mb-2">
            <div className="h-5 w-5 rounded bg-primary/20 flex items-center justify-center">
                <Search className="h-3 w-3 text-primary" />
            </div>
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Registry Search Engine</span>
        </div>
        <CommandInput 
            placeholder="Type a project, invoice, or command..." 
            className="h-12 bg-muted/50 border-border rounded-xl text-sm font-medium placeholder:text-slate-600 focus:border-primary/50 transition-all border-none focus:ring-0"
        />
      </div>
      <CommandList className="bg-[#0a0a0c]/90 backdrop-blur-3xl max-h-[450px] scrollbar-thin scrollbar-thumb-white/10 border-none">
        <CommandEmpty className="py-12 flex flex-col items-center justify-center">
            <div className="h-12 w-12 rounded-2xl bg-muted/50 border border-border flex items-center justify-center mb-3">
                <Database className="h-6 w-6 text-slate-800" />
            </div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.4em] text-slate-700">No matching records found</p>
        </CommandEmpty>
        
        <CommandGroup heading="System Actions" className="px-2">
          <CommandItem onSelect={() => runAction(onCreateQuote)} className="rounded-xl py-3 px-3 hover:bg-muted/50 cursor-pointer group">
            <div className="h-8 w-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mr-3 group-hover:bg-emerald-500 group-hover:text-black transition-all">
                <Plus className="h-4 w-4 text-emerald-500 group-hover:text-black" />
            </div>
            <div className="flex-1">
                <p className="text-xs font-medium text-foreground uppercase tracking-tight">Initiate New Project</p>
                <p className="text-[10px] text-muted-foreground font-medium">Create a fresh estimate node</p>
            </div>
            <ArrowRight className="h-3 w-3 text-slate-700 group-hover:translate-x-1 transition-transform" />
          </CommandItem>
          
          <CommandItem onSelect={() => runAction(() => onNavigate('invoices'))} className="rounded-xl py-3 px-3 hover:bg-muted/50 cursor-pointer group mt-1">
            <div className="h-8 w-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mr-3 group-hover:bg-blue-500 group-hover:text-black transition-all">
                <CreditCard className="h-4 w-4 text-blue-500 group-hover:text-black" />
            </div>
            <div className="flex-1">
                <p className="text-xs font-medium text-foreground uppercase tracking-tight">Financial Hub</p>
                <p className="text-[10px] text-muted-foreground font-medium">Manage project billing & records</p>
            </div>
            <ArrowRight className="h-3 w-3 text-slate-700 group-hover:translate-x-1 transition-transform" />
          </CommandItem>
        </CommandGroup>

        <CommandSeparator className="bg-muted/50" />

        <CommandGroup heading="Recent Estimates" className="px-2 mt-2">
          {quotations.slice(0, 5).map(q => (
            <CommandItem 
              key={q.metadata.quoteId} 
              onSelect={() => runAction(() => {
                setCurrentQuotation(q);
                onNavigate('quote-detail');
              })}
              className="rounded-xl py-3 px-3 hover:bg-muted/50 cursor-pointer group"
            >
              <div className="h-8 w-8 rounded-lg bg-muted/50 border border-border flex items-center justify-center mr-3">
                  <FileText className="h-4 w-4 text-muted-foreground group-hover:text-emerald-400 transition-colors" />
              </div>
              <div className="flex-1">
                  <p className="text-xs font-medium text-foreground uppercase tracking-tight truncate max-w-[200px]">{q.client?.projectName || 'Untitled'}</p>
                  <p className="text-[10px] text-muted-foreground font-medium">{q.metadata.quoteNumber} · {q.client?.name}</p>
              </div>
              <div className="text-right">
                  <p className="text-[10px] font-semibold text-emerald-500/80">{formatCurrency(q.financials?.grandTotal || 0)}</p>
                  <p className="text-[8px] font-medium text-slate-600 uppercase tracking-widest">{q.metadata.status}</p>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator className="bg-muted/50" />

        <CommandGroup heading="Recent Invoices" className="px-2 mt-2">
          {invoices.slice(0, 5).map(i => (
            <CommandItem 
              key={i.id} 
              onSelect={() => runAction(() => {
                window.sessionStorage.setItem('selectedInvoiceId', i.id);
                onNavigate('invoices');
              })}
              className="rounded-xl py-3 px-3 hover:bg-muted/50 cursor-pointer group"
            >
              <div className="h-8 w-8 rounded-lg bg-muted/50 border border-border flex items-center justify-center mr-3">
                  <CreditCard className="h-4 w-4 text-muted-foreground group-hover:text-blue-400 transition-colors" />
              </div>
              <div className="flex-1">
                  <p className="text-xs font-medium text-foreground uppercase tracking-tight truncate max-w-[200px]">{i.invoiceNumber || '[DRAFT]'}</p>
                  <p className="text-[10px] text-muted-foreground font-medium">{i.clientInfo.name}</p>
              </div>
              <div className="text-right">
                  <p className="text-[10px] font-semibold text-blue-500/80">{formatCurrency(i.financials.grandTotal)}</p>
                  <p className="text-[8px] font-medium text-slate-600 uppercase tracking-widest">{i.status}</p>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator className="bg-muted/50" />

        <CommandGroup heading="Intelligence & Control" className="px-2 mt-2 mb-2">
          <CommandItem onSelect={() => runAction(() => onNavigate('settings'))} className="rounded-xl py-2 px-3 hover:bg-muted/50 cursor-pointer group">
            <Settings className="h-4 w-4 text-muted-foreground mr-3 group-hover:rotate-45 transition-transform" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground group-hover:text-foreground transition-colors">System Configuration</span>
          </CommandItem>
          <CommandItem onSelect={() => runAction(() => onNavigate('clients'))} className="rounded-xl py-2 px-3 hover:bg-muted/50 cursor-pointer group mt-1">
            <Users className="h-4 w-4 text-muted-foreground mr-3 group-hover:scale-110 transition-transform" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground group-hover:text-foreground transition-colors">Client Registry</span>
          </CommandItem>
          <CommandItem className="rounded-xl py-2 px-3 hover:bg-muted/50 cursor-pointer group mt-1">
            <Sparkles className="h-4 w-4 text-primary mr-3 group-hover:animate-pulse" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-primary/80 group-hover:text-primary transition-colors">Start Ravan AI Analysis</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
      <div className="p-3 bg-[#0a0a0c] border-t border-border flex items-center justify-between">
        <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-muted/50 border border-border text-[8px] font-semibold text-muted-foreground">↑↓</kbd>
                <span className="text-[8px] font-medium text-slate-600 uppercase tracking-widest">Navigate</span>
            </div>
            <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 rounded bg-muted/50 border border-border text-[8px] font-semibold text-muted-foreground">ENTER</kbd>
                <span className="text-[8px] font-medium text-slate-600 uppercase tracking-widest">Select</span>
            </div>
        </div>
        <div className="flex items-center gap-1 opacity-50">
            <Zap className="h-2.5 w-2.5 text-primary" />
            <span className="text-[8px] font-semibold text-primary uppercase tracking-[0.3em]">Zenith Engine</span>
        </div>
      </div>
    </CommandDialog>
  );
}
